// Libraries
import _ from 'lodash';

// Supermove
import {gql} from '@supermove/graphql';
import {User, UserModel, UserMoverPositionModel} from '@supermove/models';
import {Json, withFragment, Currency, Percent, Validation} from '@supermove/utils';
import {ValidationErrors} from '@supermove/utils/src/Validation';

// App
import InviteMoverStep from '@shared/modules/User/enums/InviteMoverStep';

export interface MoverFormType {
  userId?: string;
  organizationId?: string;
  names: string[];
  phoneNumber: string;
  position: string;
  branchCode: string;
  payrollIdentifier: string;
  tabletPin: string;
  billingHourlyRate: string;
  compensationPercentage: string;
  isDriver: boolean;
  isActive: boolean;
  features: Record<string, boolean>;
  moverPositionIds: number[];
  primaryMoverPositionId?: number;
}

const getInitialMoverPositionIds = ({user}: {user: UserModel}) => {
  return User.getSortedUserMoverPositions({userMoverPositions: user.userMoverPositions}).map(
    (userMoverPosition: UserMoverPositionModel) => userMoverPosition.moverPositionId,
  );
};

const prepareFeatures = ({features}: {features: Record<string, boolean>}) => {
  return Json.stringify(Json.toUpperCaseKeys(Json.toSnakeCaseKeys(features)));
};

const _new = ({organizationId}: {organizationId: string}): MoverFormType => ({
  userId: undefined,
  organizationId,
  names: ['', ''],
  phoneNumber: '',
  position: '',
  branchCode: '',
  payrollIdentifier: '',
  tabletPin: '',
  billingHourlyRate: Currency.toForm(0),
  compensationPercentage: Percent.toForm(0),
  isDriver: false,
  isActive: true,
  features: {VIEW_CUSTOMER_INFO: false},
  moverPositionIds: [],
  primaryMoverPositionId: undefined,
});

const edit = withFragment(
  (user: UserModel): MoverFormType => ({
    userId: user.id,
    organizationId: user.organization.id,
    names: Json.toForm(user.names) as string[],
    phoneNumber: user.phoneNumber || '',
    position: user.position,
    branchCode: user.branchCode || '',
    payrollIdentifier: user.payrollIdentifier,
    tabletPin: user.tabletPin,
    billingHourlyRate: Currency.toForm(user.billingHourlyRate),
    compensationPercentage: Percent.toForm(user.compensationPercentage),
    isDriver: user.isDriver,
    isActive: user.isActive,
    features: Json.parse(user.features),
    moverPositionIds: getInitialMoverPositionIds({user}),
    primaryMoverPositionId: user.userMoverPositions.find(
      (userMoverPosition: UserMoverPositionModel) => userMoverPosition.isPrimary,
    )?.moverPositionId,
  }),
  gql`
    ${User.getSortedUserMoverPositions.fragment}

    fragment MoverForm_edit on User {
      id
      names
      phoneNumber
      payrollIdentifier
      tabletPin
      position
      branchCode
      billingHourlyRate
      compensationPercentage
      isDriver
      isActive
      features
      organization {
        id
      }
      userMoverPositions {
        id
        moverPositionId
        isPrimary
      }
      ...User_getSortedUserMoverPositions
    }
  `,
);

const toForm = ({
  userId,
  organizationId,
  names,
  phoneNumber,
  position,
  branchCode,
  payrollIdentifier,
  tabletPin,
  billingHourlyRate,
  compensationPercentage,
  isDriver,
  isActive,
  features,
  moverPositionIds,
  primaryMoverPositionId,
}: MoverFormType): MoverFormType => ({
  userId,
  organizationId,
  names,
  phoneNumber,
  position,
  branchCode,
  payrollIdentifier,
  tabletPin,
  billingHourlyRate,
  compensationPercentage,
  isDriver,
  isActive,
  features,
  moverPositionIds,
  primaryMoverPositionId,
});

const toMutation = (moverForm: MoverFormType) => ({
  userId: moverForm.userId,
  organizationId: moverForm.organizationId,
  names: Json.toMutation(moverForm.names.map((name: string) => name.trim())),
  phoneNumber: moverForm.phoneNumber,
  position: moverForm.position,
  branchCode: moverForm.branchCode,
  payrollIdentifier: moverForm.payrollIdentifier,
  tabletPin: moverForm.tabletPin,
  billingHourlyRate: Currency.toMutation(moverForm.billingHourlyRate),
  compensationPercentage: Percent.toMutation(moverForm.compensationPercentage),
  isDriver: moverForm.isDriver,
  isActive: moverForm.isActive,
  features: prepareFeatures({features: moverForm.features}),
  moverPositionIds: moverForm.moverPositionIds,
  primaryMoverPositionId: moverForm.primaryMoverPositionId,
});

// Validations
const validateUserInfo = ({form}: {form: any}): ValidationErrors => {
  const firstNameErrors = Validation.requiredStringField({
    form,
    field: 'moverForm.names.0',
    message: 'Please enter a first name.',
  });
  const phoneNumberErrors = Validation.requiredStringField({
    form,
    field: 'moverForm.phoneNumber',
    message: 'Please enter a phone number.',
  });
  const moverPositionsErrors = Validation.requiredArrayField({
    form,
    field: 'moverForm.moverPositionIds',
    message: 'Please select a position.',
  });
  const primaryPositionErrors = Validation.requiredStringField({
    form,
    field: 'moverForm.primaryMoverPositionId',
    message: 'Please select a primary position.',
  });
  // Note: tabletPin is an optional field, empty values are valid.
  const tabletPinErrors = Validation.StringFieldMinLength({
    form,
    field: 'moverForm.tabletPin',
    message: 'Please enter at least 4 digits.',
    minLength: 4,
  });
  return {
    ...firstNameErrors,
    ...phoneNumberErrors,
    ...moverPositionsErrors,
    ...primaryPositionErrors,
    ...tabletPinErrors,
  };
};

const validateFormForStep = ({stepKind, form}: {stepKind: string; form: any}): ValidationErrors => {
  switch (stepKind) {
    case InviteMoverStep.USER_INFO:
      return validateUserInfo({form});
    default:
      return {};
  }
};

const validateStep = async ({
  stepKind,
  form,
}: {
  stepKind: string;
  form: any;
}): Promise<ValidationErrors> => {
  const errors = validateFormForStep({stepKind, form});
  await form.setTouched(_.mapValues(errors, () => true));
  await form.setErrors(errors);
  return errors;
};

const MoverForm = {
  new: _new,
  edit,
  toForm,
  toMutation,

  // Helpers
  validateStep,
};

export default MoverForm;
